/**
 * Minimal SSE hook for chat progress updates.
 * Simple implementation: connect, receive messages, close on COMPLETE.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { apiUrl } from "@app/const";
import { getAccessToken } from "@app/api/getAccessToken";
import { store } from "@app/redux/store";
import type {
  ProgressMessage,
  ThinkItemMessage,
  ContentMessage,
} from "@app/api/carApi.manual";
import { MessageType } from "@app/api/carApi.manual";

// SSE connection timeout (30 seconds - if no data received, consider it failed)
const SSE_CONNECTION_TIMEOUT_MS = 30000;
// SSE inactivity timeout (5 minutes - if no data received for this long, close connection)
const SSE_INACTIVITY_TIMEOUT_MS = 300000;

export interface UseChatProgressReturn {
  progress: ProgressMessage | null;
  isConnected: boolean;
  error: Error | null;
  currentStep: string | null;
  lastThinkItem: ThinkItemMessage | null; // Keep last think_item for UI display
  close: () => void;
}

export const useChatProgress = (
  planResultRootId: string,
  planResultTypeId: string,
  requestId: string | null
): UseChatProgressReturn => {
  const [progress, setProgress] = useState<ProgressMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [lastThinkItem, setLastThinkItem] = useState<ThinkItemMessage | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const close = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsConnected(false);
    // Don't clear lastThinkItem - keep it for UI display
  }, []);

  useEffect(() => {
    if (!requestId) {
      // Clear lastThinkItem when requestId is cleared
      setLastThinkItem(null);
      return;
    }

    // Close any existing connection and clear lastThinkItem for new request
    setLastThinkItem(null);
    close();
    setProgress(null);
    setCurrentStep(null);
    setError(null);

    const connectSSE = async () => {
      const sseStartTime = Date.now();
      let lastDataReceivedTime = Date.now();
      let connectionTimeoutId: NodeJS.Timeout | null = null;
      let inactivityTimeoutId: NodeJS.Timeout | null = null;
      let firstThinkItemReceived = false; // Track if we've received the first think_item
      let messageCount = 0;
      
      try {
        const accessToken = await getAccessToken(store);
        if (!accessToken) {
          console.error("[SSE] No access token available");
          setError(new Error("No access token available"));
          return;
        }

        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const url = `${baseUrl}/api/v1/translucence/${planResultRootId}/${planResultTypeId}/chat/progress/${requestId}/stream/`;

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        console.log("[SSE] Connecting to:", url);
        const fetchStartTime = Date.now();
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
          signal: abortController.signal,
        });

        const fetchEndTime = Date.now();
        console.log(`[SSE] Connection established in ${fetchEndTime - fetchStartTime}ms, status: ${response.status}`);

        if (!response.ok) {
          console.error(`[SSE] Connection failed with status ${response.status}`);
          setError(new Error(`SSE connection failed: ${response.status}`));
          setIsConnected(false);
          return;
        }

        const connectionEstablishedTime = Date.now();
        setIsConnected(true);
        setError(null);

        const reader = response.body?.getReader();
        if (!reader) {
          console.error("[SSE] No response body reader available");
          setError(new Error("No response body reader available"));
          return;
        }

        // Set connection timeout - if no data received within this time, consider connection failed
        connectionTimeoutId = setTimeout(() => {
          const timeSinceStart = Date.now() - sseStartTime;
          if (!firstThinkItemReceived) {
            console.warn(`[SSE] No data received within ${SSE_CONNECTION_TIMEOUT_MS}ms. Connection may be stalled.`);
            console.warn(`[SSE] Time since connection: ${timeSinceStart}ms`);
            // Don't abort immediately - backend might be slow, but log the issue
          }
        }, SSE_CONNECTION_TIMEOUT_MS);

        // Set inactivity timeout - if no data received for this long, close connection
        inactivityTimeoutId = setTimeout(() => {
          const timeSinceLastData = Date.now() - lastDataReceivedTime;
          console.warn(`[SSE] No data received for ${timeSinceLastData}ms. Closing connection due to inactivity.`);
          setIsConnected(false);
          abortController.abort();
        }, SSE_INACTIVITY_TIMEOUT_MS);

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (abortController.signal.aborted || done) {
            console.log(`[SSE] Stream closed. Total messages received: ${messageCount}`);
            setIsConnected(false);
            break;
          }

          // Update last data received time
          lastDataReceivedTime = Date.now();
          
          // Reset inactivity timeout since we received data
          if (inactivityTimeoutId) {
            clearTimeout(inactivityTimeoutId);
            inactivityTimeoutId = setTimeout(() => {
              const timeSinceLastData = Date.now() - lastDataReceivedTime;
              console.warn(`[SSE] No data received for ${timeSinceLastData}ms. Closing connection due to inactivity.`);
              setIsConnected(false);
              abortController.abort();
            }, SSE_INACTIVITY_TIMEOUT_MS);
          }

          buffer += decoder.decode(value, { stream: true });

          // Process SSE events (separated by \n\n)
          const eventSeparator = "\n\n";
          let eventEndIndex = buffer.indexOf(eventSeparator);

          while (eventEndIndex !== -1) {
            const eventText = buffer.substring(0, eventEndIndex);
            buffer = buffer.substring(eventEndIndex + eventSeparator.length);

            // Extract data from "data: ..." lines
            const lines = eventText.split("\n");
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("data:")) {
                const dataContent = line.slice(5).trimStart();
                eventData = eventData ? `${eventData}\n${dataContent}` : dataContent;
              }
            }

            if (eventData) {
              messageCount++;
              try {
                const message = JSON.parse(eventData) as ProgressMessage;
                console.log(`[SSE] Received message #${messageCount}, type: ${message.type}`);

                // Clear connection timeout on first message
                if (connectionTimeoutId) {
                  clearTimeout(connectionTimeoutId);
                  connectionTimeoutId = null;
                }

                // Handle different message types
                if (message.type === MessageType.CONNECTED) {
                  console.log("[SSE] Connection confirmed by server");
                  // Just log, don't set as progress
                } else if (message.type === MessageType.COMPLETE || message.type === MessageType.ERROR) {
                  console.log(`[SSE] Received ${message.type} message. Closing connection.`);
                  // Set progress to COMPLETE/ERROR so useChat can process it, then close connection
                  setProgress(message);
                  setIsConnected(false);
                  if (connectionTimeoutId) clearTimeout(connectionTimeoutId);
                  if (inactivityTimeoutId) clearTimeout(inactivityTimeoutId);
                  abortController.abort();
                  return;
                } else {
                  // Set progress for think_item, content, etc.
                  setProgress(message);

                  // Update current step and last think_item
                  if (message.type === MessageType.THINK_ITEM) {
                    const thinkItem = message as ThinkItemMessage;
                    
                    // Only log timing for the FIRST think_item
                    if (!firstThinkItemReceived) {
                      firstThinkItemReceived = true;
                      const thinkItemReceivedTime = Date.now();
                      const timeFromSSEStart = thinkItemReceivedTime - sseStartTime;
                      console.log(`[SSE] First think_item received after ${timeFromSSEStart}ms`);
                    }
                    
                    setLastThinkItem(thinkItem);
                    if (thinkItem.step) {
                      setCurrentStep(thinkItem.step);
                    }
                  } else if (message.type === MessageType.CONTENT) {
                    const contentMessage = message as ContentMessage;
                    if (contentMessage.step) {
                      setCurrentStep(contentMessage.step);
                    }
                  }
                }
              } catch (err) {
                console.error("[SSE] Failed to parse message:", err, eventData);
              }
            }

            eventEndIndex = buffer.indexOf(eventSeparator);
          }
        }
      } catch (err) {
        // Clean up timeouts
        if (connectionTimeoutId) clearTimeout(connectionTimeoutId);
        if (inactivityTimeoutId) clearTimeout(inactivityTimeoutId);
        
        if (err instanceof Error && err.name === "AbortError") {
          // Expected when connection is closed
          console.log("[SSE] Connection aborted (expected)");
          setIsConnected(false);
          return;
        }

        console.error("[SSE] Connection error:", err);
        setIsConnected(false);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    };

    connectSSE();

    return () => {
      close();
    };
  }, [requestId, planResultRootId, planResultTypeId, close]);

  return {
    progress,
    isConnected,
    error,
    currentStep,
    lastThinkItem,
    close,
  };
};
